import { EmailTo } from 'components/EmailTo'
import { COMPANY_DNS } from 'config'
import { EmailTemplates } from 'pages/Admin/ConditionsAndTemplates/EmailTemplates'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { partiesMapWithAccountTypes } from 'reducers/loanDetail.reducer'
import { getBorrowerInfo, sendLoanProcessDocumentEmail } from 'services'
import { DocumentFile, InputFileTable, Modal, TextArea } from 'stories/components'
import { borrowerEmailParties, emailDirDefaults } from 'utils/borrowerRecipients'
import { setLoanNumber } from 'utils/setLoanNumber'

import { LoanSubmissionDocument } from '../types'
// import { formatTime } from 'utils'

export const LoanSendEmailDialog = ({
  onClose,
  documents: _documents,
}: {
  onClose: Function
  documents: LoanSubmissionDocument[]
}) => {
  const { loanDetail } = useSelector((state: any) => ({
    loanDetail: state.loanDetail,
    borrower: state.borrower.borrower,
  }))
  const { parties, emailContacts } = loanDetail
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<{ party: string; email: string }[]>([])

  const [selectedEmails, setSelectedEmails] = useState<Record<string, 'to' | 'cc' | 'bcc' | ''>>({})
  const [note, setNote] = useState('')
  const [documents, setDocuments] = useState<DocumentFile[]>([])
  const [showTemplate, setShowTemplate] = useState(false)
  const loanNumber = setLoanNumber()

  useEffect(() => {
    const documents = _documents.map((file) => ({
      ...file,
      fileKey: file.key,
      id: file.DocumentID,
      status: 'Not Reviewed',
    })) as DocumentFile[]
    setDocuments(documents)
  }, [])

  const partiesMap = partiesMapWithAccountTypes()

  useEffect(() => {
    setLoading(true)
    getBorrowerInfo()
      .then(async ({ parties, borrowerEmail, borrowerAdditionalEmails }) => {
        // Offer and address every address the borrower keeps, not just the primary.
        const borrowerParties = borrowerEmailParties({
          email: borrowerEmail,
          additionalEmails: borrowerAdditionalEmails,
        })
        const emails: { party: string; email: string }[] = [...borrowerParties]
        Object.keys(parties).forEach((key) => {
          if (key == 'accountManager') return
          let email = parties[key]
          if (!email) return

          emails.push({
            party: partiesMap[key],
            email,
          })
        })
        setData(emails)
        setSelectedEmails(emailDirDefaults(borrowerParties.map((row) => row.email)))
      })
      .finally(() => setLoading(false))
  }, [])

  const onSubmit = async () => {
    const hasToEmail = !!Object.keys(selectedEmails).filter((email) => selectedEmails[email] == 'to').length
    if (!hasToEmail) {
      toast('Please choose one more TO email.', { type: 'error' })
      return
    }

    let hasError = false
    if (hasError) return

    setLoading(true)

    await sendLoanProcessDocumentEmail(selectedEmails, note, documents)

    await onClose(true, selectedEmails)
    toast('Email is sent successfully.', { type: 'success' })
    setLoading(false)
  }

  useEffect(() => {
    const emails: { party: string; email: string }[] = []
    Object.keys(parties).map((key) => {
      let { email } = parties[key]
      email = key === 'creator' ? email : loanDetail.parties[key].email
      if (!email) return

      emails.push({
        party: partiesMap[key],
        email,
      })
    })
    emails.push(
      ...emailContacts.map((email: string) => ({
        party: 'E-Mail Notification Contacts',
        email,
      })),
    )
    emails.push({ party: 'Loan Setup', email: `loansetup@${COMPANY_DNS}` })
    emails.push({ party: 'Closer', email: `closing@${COMPANY_DNS}` })
    setData(emails)
  }, [parties, emailContacts])

  const onChooseTemplate = (template: any) => {
    setNote(template.content || '')
    setShowTemplate(false)
  }

  return (
    <>
      <Modal
        title={`Send Emails to ...`}
        titleOkay="Send Email"
        isOpen
        loading={loading}
        disabled={!Object.keys(selectedEmails).length || !Object.keys(documents).length || !note.trim()}
        lastUpdatedAt={Date.now()}
        onClose={() => onClose(false)}
        onOk={() => onSubmit()}
        childLevel={1}
      >
        <div className="w-[calc(100vw-5rem)] max-w-[36rem]">
          <EmailTo Emails={selectedEmails} Data={data} onChange={(data: any) => setSelectedEmails(data)} />

          <div className="mt-4">
            <TextArea
              className=""
              title={'Notes'}
              key={'Notes'}
              rows={3}
              required
              value={note}
              onChange={(value: string) => setNote(value)}
              additionalElements={
                <span className="text-shade-blue hover:underline cursor-pointer" onClick={() => setShowTemplate(true)}>
                  Choose Template
                </span>
              }
            />
          </div>
          <InputFileTable
            title="Attachment Files"
            filePath={`/loan/${loanNumber}/submission`}
            showStatus={false}
            showCategory={false}
            value={documents}
            onChange={setDocuments}
          />
        </div>
      </Modal>
      {showTemplate && (
        <Modal title="Email Template" titleOkay="" childLevel={2} onClose={() => setShowTemplate(false)} isOpen>
          <div className="w-[calc(100vw-7rem)] max-w-[600px]">
            <EmailTemplates isModal onRawClick={onChooseTemplate} />
          </div>
        </Modal>
      )}
    </>
  )
}
